import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";
import { useTable } from "@/app/hooks/useTable";
import { selfHireService } from "@/app/services/selfHireService";
import { useToast } from "@/view/components/ui/use-toast";
import debounce from "lodash.debounce";
import { twMerge } from "tailwind-merge";
import { Table } from "@/app/entities/Table";
import { SelectTables } from "@/view/pages/Links/components/SelectTables";

interface CreateLinkModalProps {
  visible: boolean;
  newLinkName: string;
  setNewLinkName: (name: string) => void;
  selectedTables: string[];
  setSelectedTables: (tables: string[]) => void;
  onCreateLink: () => void;
  isLoading: boolean;
  onClose: () => void;
}

interface Option {
  value: string;
  label: string;
  subOptions?: Option[];
}

const CreateLinkModal = ({
  visible,
  newLinkName,
  setNewLinkName,
  selectedTables: _selectedTables,
  setSelectedTables: _setSelectedTables,
  onCreateLink,
  isLoading,
  onClose,
}: CreateLinkModalProps) => {
  const {
    tables,
    isLoadingTables,
  } = useTable();

  const [options, setOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isNameValid, setIsNameValid] = useState<boolean | null>(null)
  const [isCheckingName, setIsCheckingName] = useState(false)

  const { toast } = useToast();

  useEffect(() => {
    if (!visible) {
      setInputValue('')
      setIsNameValid(false)
      setIsCheckingName(false)
    }
  }, [visible])

  const checkNameAvailability = useCallback(
    async (name: string) => {
      if (!name.trim()) {
        setIsNameValid(false)
        return
      }

      setIsCheckingName(true)
      try {
        const isAvailable = await selfHireService.checkName(name.trim())
        setIsNameValid(isAvailable)

        if (isAvailable) setNewLinkName(name.trim())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Erro ao verificar nome:', error)
        setIsNameValid(false)
      } finally {
        setIsCheckingName(false)
      }
    },
    [setNewLinkName],
  )

  const debouncedCheckName = useRef(
    debounce(checkNameAvailability, 2000),
  ).current

  const handleNameChange = (text: string) => {
    setInputValue(text)
    setIsNameValid(null)

    if (text.trim()) {
      debouncedCheckName(text)
    } else {
      setIsNameValid(null)
      setNewLinkName('')
    }
  }

  const isButtonDisabled =
    !newLinkName.trim() ||
    !isNameValid ||
    isCheckingName ||
    isLoading ||
    inputValue.trim() === '' ||
    _selectedTables.length === 0;

  function handleOptions(items: Table[]) {
    const byType = items.reduce<Record<string, Table[]>>((acc, item) => {
      acc[item.type || ""] = [...(acc[item.type || ""] || []), item];
      return acc;
    }, {})

    const options = Object.entries(byType).map(([type, group]) => {
      if (group.length === 1) {
        return {
          value: group[0]._id,
          label: group[0].name,
          commission: group[0].percents,
        }
      }
      return {
        value: type,
        label: type,
        subOptions: group.map((item) => ({
          value: item._id,
          label: item.name,
          commission: item.percents,
        })),
      }
    })
    setOptions(options);
  }

  const handleSubmit = () => {
    if (options.length === 0) {
      toast({
        variant: "destructive",
        title: "Selecione pelo menos uma tabela",
      });
      return;
    }
    onCreateLink();
  }

  useEffect(() => {
    if (tables && tables.length > 0) {
      handleOptions(tables || []);
    }
  }, [tables]);

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar link de autocontratação</DialogTitle>
          <DialogDescription>
            Use esse link para divulgar e acompanhar seus resultados
          </DialogDescription>
        </DialogHeader>
        <div className="mb-4">
          <p>Nome</p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Defina um nome para seu link"
            className={twMerge(
              "mb-4 w-full rounded-lg border border-gray01 p-2",
              isNameValid && "border-success",
            )}
          />
          {isNameValid === false && !isCheckingName && (
            <p className="mb-4 text-xs font-medium text-error">
              Nome já está em uso
            </p>
          )}
          <div
            className="mb-4"
          >
            {isLoadingTables ? (
              <p>Carregando tabelas...</p>
            ) : (
              <SelectTables
                setSelectedTables={_setSelectedTables}
                selectedTables={_selectedTables}
                options={options}
              />
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isButtonDisabled}
            className="mt-4 w-full rounded-full bg-primary text-white"
          >
            {isLoading ? "Criando link..." : "Criar link auto contratação"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLinkModal;
